import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Wallet, Check, X } from 'lucide-react';
import { useGameWallet } from '../providers/GameWalletProvider';

const WalletTrustBadge = ({ compact = false }) => {
  const { isWalletActive, credits } = useGameWallet();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close tooltip on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowTooltip(false);
    };
    if (showTooltip) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showTooltip]);

  const TooltipContent = () => (
    <div className="bg-[#0a0a0a] border-2 border-green-500/50 rounded-xl p-4 shadow-2xl shadow-green-500/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-green-400" />
          <span className="font-bold text-green-400 text-sm">FAST-PLAY WALLET</span>
        </div>
        <button
          onClick={() => setShowTooltip(false)}
          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          data-testid="close-tooltip"
        >
          <X className="w-5 h-5 text-white/60" />
        </button>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg bg-green-500/10 border border-green-500/30">
        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
        <span className="text-green-300 text-sm">Deposits are safe.</span>
      </div>

      {/* Features */}
      <ul className="space-y-2.5 text-sm text-white/70 mb-4">
        <li className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
          No signatures required for gameplay
        </li>
        <li className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
          Instant transactions, no pop-ups
        </li>
        <li className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
          Withdraw anytime to your wallet
        </li>
      </ul>

      {/* Current Balance */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-black/50 border border-white/10">
        <span className="text-white/60 text-sm">Your Credits:</span>
        <span className="font-bold text-cyan-400 text-lg">{credits.toLocaleString()}</span>
      </div>
    </div>
  );

  // Mobile: Use Portal to render at body level as bottom sheet
  const MobileTooltip = () => createPortal(
    <AnimatePresence>
      {showTooltip && (
        <>
          {/* Full screen backdrop */}
          <motion.div 
            className="fixed inset-0 bg-black/70 z-[9998]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTooltip(false)}
          />
          {/* Bottom sheet - with safe area padding */}
          <motion.div
            className="fixed left-0 right-0 bottom-0 z-[9999] px-4 pb-8 pt-2"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Drag handle */}
            <div className="flex justify-center mb-2">
              <div className="w-10 h-1 bg-white/30 rounded-full" />
            </div>
            <TooltipContent />
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );

  // Desktop: Regular dropdown
  const DesktopTooltip = () => (
    <AnimatePresence>
      {showTooltip && (
        <>
          {/* Light backdrop for desktop */}
          <motion.div 
            className="fixed inset-0 z-[98]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTooltip(false)}
          />
          <motion.div
            className="absolute top-full right-0 mt-2 w-80 z-[99]"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <TooltipContent />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <div className="relative">
      <motion.button
        onClick={() => setShowTooltip(!showTooltip)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
          isWalletActive
            ? 'bg-green-500/20 border border-green-500/50 hover:border-green-400'
            : 'bg-gray-500/20 border border-gray-500/50 hover:border-gray-400'
        } ${compact ? 'px-2 py-1' : ''}`}
        whileTap={{ scale: 0.95 }}
        data-testid="wallet-trust-badge"
      >
        {/* Shield/Wallet Icon with Status Dot */}
        <div className="relative">
          <Shield className={`w-4 h-4 ${isWalletActive ? 'text-green-400' : 'text-gray-400'}`} />
          <motion.div
            className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
              isWalletActive ? 'bg-green-400' : 'bg-gray-400'
            }`}
            animate={isWalletActive ? { scale: [1, 1.3, 1], opacity: [1, 0.7, 1] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
        
        {/* Status Text */}
        {!compact && (
          <span className={`text-xs font-bold uppercase tracking-wide ${
            isWalletActive ? 'text-green-400' : 'text-gray-400'
          }`}>
            {isWalletActive ? 'FAST-PLAY: ACTIVE' : 'WALLET: INACTIVE'}
          </span>
        )}
      </motion.button>

      {/* Render tooltip based on viewport */}
      {isMobile ? <MobileTooltip /> : <DesktopTooltip />}
    </div>
  );
};

export default WalletTrustBadge;
